import Common from '../../../shared/Common';
import { IExpenseCategoryData, IGenericFinancialValue } from './types';

export function sumBy<T>(items: T[], selector: (item: T) => number): number {
  return items.reduce((acc, item) => acc + selector(item), 0);
}

export function averageBy<T>(items: T[], selector: (item: T) => number): number {
  if (items.length === 0) {
    return 0;
  }

  return sumBy(items, selector) / items.length;
}

interface PrepareFinancialDataOptions {
  includeFuture?: boolean;
  includeCurrentMonth?: boolean;
  showLastMonths?: boolean;
  lastMonthsCount?: number;
  includeTaxes?: boolean;
  isWeekly?: boolean;
}

export function prepareFinancialData(
  data: IGenericFinancialValue[],
  options: PrepareFinancialDataOptions
): IGenericFinancialValue[] {
  const { includeFuture, includeCurrentMonth, showLastMonths, lastMonthsCount, includeTaxes, isWeekly } = options;

  let currentDate = new Date();
  if (!includeFuture) {
    if (!includeCurrentMonth) {
      currentDate = Common.Utils.getPreviousMonthDate(currentDate);
    }
    if (isWeekly) {
      data = data.filter((e) => currentDate >= Common.Utils.getDateOfIsoWeek(e.week, e.year));
    } else {
      data = data.filter((e) => currentDate >= new Date(e.year, e.month - 1));
    }
  }
  if (showLastMonths) {
    const currentDate = new Date();
    if (isWeekly) {
      const monthsAgo = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - ((lastMonthsCount ?? 1) + 1),
        currentDate.getDate()
      );
      data = data.filter(
        (e) =>
          new Date() >= Common.Utils.getDateOfIsoWeek(e.week, e.year) &&
          Common.Utils.getDateOfIsoWeek(e.week, e.year) >= monthsAgo
      );
    } else {
      const monthsAgo = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - ((lastMonthsCount ?? 1) + 1),
        currentDate.getDate()
      );
      data = data.filter((e) => new Date(e.year, e.month - 1) >= monthsAgo);
    }
  }

  data = data.map((item) => ({
    ...item,
    totalExpense: includeTaxes ? item.totalExpense : item.totalExpense - item.totalTax,
    totalIncome: includeTaxes ? item.totalIncome : item.totalIncome - item.totalTax,
    balance: Number((item.totalIncome - item.totalExpense).toFixed(2)),
  }));

  return data;
}

export function prepareExpenseCategoryData(
  data: IExpenseCategoryData[],
  options: PrepareFinancialDataOptions
): IExpenseCategoryData[] {
  const { includeFuture, includeCurrentMonth, showLastMonths, lastMonthsCount } = options;

  let currentDate = new Date();
  if (!includeFuture) {
    if (!includeCurrentMonth) {
      currentDate = Common.Utils.getPreviousMonthDate(currentDate);
    }
    data = data.filter((e) => currentDate >= new Date(e.year, e.month - 1));
  }
  if (showLastMonths) {
    const currentDate = new Date();
    const monthsAgo = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - ((lastMonthsCount ?? 1) + 1),
      currentDate.getDate()
    );
    data = data.filter((e) => new Date(e.year, e.month - 1) >= monthsAgo);
  }

  return data;
}
